import React, { Dispatch, SetStateAction } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import UnitFinishEarlyDropdown from '../../../components/app-agent/UnitFinishEarlyDropdown';
import { useAuth } from '../../../context/Auth';
import FormInput from '../../../components/FormInput';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    onClose: () => void;
    onSubmit: () => void;
    finishEarlyReason: number;
    setFinishEarlyReason: Dispatch<SetStateAction<number>>;
    needFinishReason: boolean;
    unitId: number;
    dailyPoliceLog: string;
    setDailyPoliceLog: Dispatch<SetStateAction<string>>;
    observations: string;
    setObservations: Dispatch<SetStateAction<string>>;
}

export default function FinishUnitModal({ onClose, onSubmit, finishEarlyReason, setFinishEarlyReason, needFinishReason, isModalVisible, setModalVisible, unitId, dailyPoliceLog, setDailyPoliceLog, observations, setObservations }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    if (!authenticatedUser || unitId === 0) {
        return;
    }

    function isSaveButtonDisabled() {
        if (dailyPoliceLog === '') {
            return true;
        }

        if (needFinishReason && !finishEarlyReason) {
            return true;
        }

        return false;
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                setModalVisible(false);
                setFinishEarlyReason(0);
            }}
        >
            <View style={styles.container}>
                <View style={styles.formContainer}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('unitDeactivation')}</MyAppText>
                        <MyAppText style={styles.subheader}>{translate('fillFieldsBelow')}</MyAppText>
                    </View>
                    {needFinishReason && <View style={[styles.viewStyle, { zIndex: 2 }]}>
                        <View style={[{ flexDirection: 'row', columnGap: 5 }]}>
                            <MyAppText style={styles.inputLabelStyle}>{translate('unitDeactivationReason')}</MyAppText>
                            {needFinishReason ?
                                <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                : null}
                        </View>
                        <UnitFinishEarlyDropdown
                            label=''
                            value={finishEarlyReason}
                            setValue={setFinishEarlyReason}
                            unitId={unitId}
                        />
                    </View>}
                    <FormInput
                        label={translate('dailyPolicingLog')}
                        placeholder={translate('dailyPolicingLog')}
                        value={dailyPoliceLog}
                        required
                        multiline={true}
                        onChangeText={(val) => setDailyPoliceLog(val)}
                        labelStyle={styles.inputLabelStyle}
                        viewStyle={styles.viewStyle}
                    />

                    <FormInput
                        label={translate('observations')}
                        placeholder={translate('observations')}
                        value={observations}
                        multiline={true}
                        onChangeText={(val) => setObservations(val)}
                        labelStyle={styles.inputLabelStyle}
                        viewStyle={styles.viewStyle}
                    />
                    <FormActions
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9 }}
                        onSubmit={() => {
                            onSubmit();
                            setModalVisible(false);
                            onClose();
                        }}
                        onClose={() => {
                            setModalVisible(false);
                            onClose();
                        }}
                        disabled={isSaveButtonDisabled()} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: {
        minWidth: 300,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    inputLabelStyle: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        marginBottom: 8,
    },
    viewStyle: {
        flexDirection: 'column',
        rowGap: 8
    }
}));
